import type { FaqItem } from "@/components/editorial/faq";
import type { LandingHeroContent } from "@/components/editorial/landing-hero";

import type { ProofKind } from "@/components/product-shot/proof";

import { CREATIVE_MEDIA } from "./content/creative-media";
import { FINTECH } from "./content/fintech";
import { FOOD_CONSUMER_GOODS } from "./content/fmcg";
import { FRACTIONAL } from "./content/fractional";
import { HOTELS } from "./content/hotels";
import { PE_VC } from "./content/pe-vc";
import { TECHNOLOGY } from "./content/technology";

/**
 * The solution pages beyond the two sector pages under /for/ (Accountancy
 * firms and Agencies & consultancies), each at /solutions/<slug> and listed
 * in the site's Solutions menu. A page is the sector's hero, a replay of its
 * key question in a sample workspace (stories/), three outcomes, how a first
 * workflow starts, the sector's questions and the close.
 *
 * Copy discipline: claim only what the product does today, keep
 * professional judgement with the customer, and say where a workflow stops.
 * Each sector names the demo organisation on demo.mlx.systems to seed so a
 * live demo matches its page.
 */
export type SolutionSector = {
  slug: string;
  /** The menu's name for the sector. */
  name: string;
  /** The demo organisation whose seeded content matches the page. */
  demoOrg: string;
  /** The page's replay (stories/replays.ts). */
  story: string;
  metadata: { title: string; description: string };
  hero: LandingHeroContent;
  replay: { title: string; lead: string };
  /** Each outcome with the piece of the product that shows it (components/product-shot/proof.tsx). */
  outcomes: { title: string; lead: string; items: readonly { title: string; body: string; show: ProofKind }[] };
  /** The first of the three steps: which review to start with. */
  start: { firstStep: string };
  questions: readonly FaqItem[];
  closing: string;
};

export const SOLUTION_SECTORS: readonly SolutionSector[] = [
  CREATIVE_MEDIA,
  PE_VC,
  TECHNOLOGY,
  FINTECH,
  FOOD_CONSUMER_GOODS,
  HOTELS,
  FRACTIONAL,
];

export function solutionHref(sector: Pick<SolutionSector, "slug">): `/solutions/${string}` {
  return `/solutions/${sector.slug}`;
}

export function findSolutionSector(slug: string): SolutionSector | undefined {
  return SOLUTION_SECTORS.find((sector) => sector.slug === slug);
}

/** A sector by slug, for its own route: a missing slug is a coding error. */
export function solutionSector(slug: string): SolutionSector {
  const sector = findSolutionSector(slug);
  if (!sector) throw new Error(`No solution sector with the slug "${slug}".`);
  return sector;
}
